import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Alert, Box, Tab, TabList, Tabs, Typography } from "@mui/joy";
import { Person as PersonIcon } from "@mui/icons-material";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import AddIcon from "@mui/icons-material/Add";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CircularProgress from "@mui/material/CircularProgress";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";

import { fetchUserProfile } from "../../redux/authReducer";
import ProfileHeader from "./ProfileHeader";
import ProfileTab from "./ProfileTab";
import IntegrationsTab from "./IntegrationsTab";
import UserManagementTab from "./UserManagementTab";
import ModelsTab from "./ModelsTab";

const swipeVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
  }),
};

const resolveTabIndexFromQuery = (tabKey, role) => {
  switch (tabKey) {
    case "profile":
      return 0;
    case "api_keys":
      return 1;
    case "users":
      return role === "admin" ? 2 : null;
    case "models":
      return role === "admin" ? 3 : 2;
    default:
      return null;
  }
};

const ProfilePage = () => {
  const { user, isLoading } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const [[activeTab, direction], setActiveTab] = useState([0, 0]);
  const prevTabRef = useRef(0);

  const handleTabChange = (_, newValue) => {
    const newDirection = newValue > prevTabRef.current ? 1 : -1;
    prevTabRef.current = newValue;
    setTabIndex(newValue);
    setActiveTab([newValue, newDirection]);
  };

  const company = user?.company ?? null;
  const searchParams = new URLSearchParams(location.search);
  const queryTab = searchParams.get("tab");
  const onboardingMode = searchParams.get("onboarding") === "1";

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchUserProfile(user.id));
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (!queryTab || !user?.role) return;

    const nextTab = resolveTabIndexFromQuery(queryTab.toLowerCase(), user.role);
    if (nextTab === null || nextTab === tabIndex) return;

    const newDirection = nextTab > prevTabRef.current ? 1 : -1;
    prevTabRef.current = nextTab;
    setTabIndex(nextTab);
    setActiveTab([nextTab, newDirection]);
  }, [queryTab, setActiveTab, tabIndex, user?.role]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100%",
        bgcolor: "background.surface",
        p: 0,
      }}
    >
      <Box sx={{ flex: 1, p: 3 }}>
        <ProfileHeader user={user} />

        {onboardingMode && (
          <Alert color="primary" variant="soft" sx={{ mb: 2, alignItems: "flex-start" }}>
            <Box>
              <Typography level="title-sm">Onboarding Step 1 of 2</Typography>
              <Typography level="body-sm">
                Add your first LLM model. After creating it, you&apos;ll continue in Agents to build your first policy from an example document.
              </Typography>
            </Box>
          </Alert>
        )}

        <Tabs value={tabIndex} onChange={handleTabChange}>
          <TabList
            sx={{
              borderBottom: "1px solid var(--joy-palette-neutral-outlinedBorder)",
              px: 2,
              bgcolor: "background.surface",
            }}
          >
            <Tab value={0}>
              <PersonIcon fontSize="small" sx={{ mr: 1 }} /> Profile
            </Tab>
            <Tab value={1}>
              <SettingsInputComponentIcon fontSize="small" sx={{ mr: 1 }} />{" "}
              Api keys
            </Tab>
            {user.role === "admin" && (
              <Tab value={2}>
                <AddIcon fontSize="small" sx={{ mr: 1 }} /> User Management
              </Tab>
            )}
            {(user.role === "admin" || user.role === "user") && (
              <Tab value={user.role === "admin" ? 3 : 2}>
                <SmartToyIcon fontSize="small" sx={{ mr: 1 }} /> LLM Models
              </Tab>
            )}
          </TabList>

          <Box sx={{ position: "relative", overflow: "hidden", p: 3 }}>
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={activeTab}
                custom={direction}
                variants={swipeVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
              >
                {activeTab === 0 && <ProfileTab user={user} company={company} />}
                {activeTab === 1 && <IntegrationsTab />}
                {activeTab === 2 && user.role === "admin" && (
                  <UserManagementTab company={company} />
                )}
                {activeTab === (user.role === "admin" ? 3 : 2) &&
                  (user.role === "admin" || user.role === "user") && (
                    <ModelsTab readOnly={false} />
                  )}
              </motion.div>
            </AnimatePresence>
          </Box>
        </Tabs>
      </Box>
    </Box>
  );
};

export default ProfilePage;
